import { useEffect, useRef, useState } from "react";
import { IoSaveOutline } from "react-icons/io5";
import FieldInputRenderer from "../fields/FieldInputRenderer";
import {
  EMPTY_POSITION_FORM,
  POSITION_DETAIL_FIELD_DEFS,
  POSITION_FIELD_DEFS,
  POSITION_LIST_FIELD_DEFS,
  slugifyTitle,
} from "./positionFieldUtils";

export default function PositionFormModal({
  open,
  initialForm,
  editingPositionKey = null,
  isEdit = false,
  busy,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(EMPTY_POSITION_FORM);
  const initialSnapshotRef = useRef(EMPTY_POSITION_FORM);

  useEffect(() => {
    if (!open) return;
    const snapshot = initialForm
      ? { ...EMPTY_POSITION_FORM, ...initialForm }
      : { ...EMPTY_POSITION_FORM };
    initialSnapshotRef.current = snapshot;
    setForm(snapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingPositionKey]);

  if (!open) return null;

  const hasChanges = POSITION_FIELD_DEFS.some(
    (def) =>
      (form[def.suffix] ?? "") !==
      (initialSnapshotRef.current[def.suffix] ?? "")
  );

  function setField(suffix, value) {
    setForm((prev) => ({ ...prev, [suffix]: value }));
  }

  function handleTitleBlur() {
    if (isEdit) return;
    if (!form.slug.trim() && form.title.trim()) {
      setForm((prev) => ({ ...prev, slug: slugifyTitle(prev.title) }));
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit({
      ...form,
      slug: form.slug.trim().toLowerCase(),
    });
  }

  function renderField(suffix) {
    const def = POSITION_FIELD_DEFS.find((d) => d.suffix === suffix);
    if (!def) return null;

    return (
      <FieldInputRenderer
        key={suffix}
        field={{
          key: suffix,
          label: def.label,
          fieldType: def.fieldType,
          isRequired: suffix === "title" || suffix === "slug",
        }}
        value={form[suffix] ?? ""}
        onChange={(next) => setField(suffix, next)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
      />
      <form
        onSubmit={handleSubmit}
        className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
      >
        <div className="border-b border-gray-100 px-6 py-5">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEdit ? "Edit position" : "New position"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {isEdit
              ? "Update listing and job detail content for this role."
              : "Create a new open position for the Careers page."}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="space-y-8">
            <div className="grid gap-4 md:grid-cols-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 md:col-span-2">
                Listing
              </p>
              {POSITION_LIST_FIELD_DEFS.map((def) => (
                <div
                  key={def.suffix}
                  onBlur={def.suffix === "title" ? handleTitleBlur : undefined}
                >
                  {renderField(def.suffix)}
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Job detail page
              </p>
              {POSITION_DETAIL_FIELD_DEFS.map((def) => renderField(def.suffix))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              busy ||
              (isEdit
                ? !hasChanges
                : !form.title.trim() || !form.slug.trim())
            }
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            <IoSaveOutline className="h-4 w-4" />
            {busy ? "Saving…" : isEdit ? "Save position" : "Create position"}
          </button>
        </div>
      </form>
    </div>
  );
}
